/**
 * DiagnosisInput — Input con autosuggest de diagnósticos (CIE-10) para la
 * consulta y detalle del paciente. Hace matching difuso contra `COMMON_DIAGNOSES`
 * y permite seleccionar con teclado (↑ ↓ Enter Esc) o click.
 */
import { useEffect, useRef, useState } from "react";
import { Search, X } from "@/components/icons";
import { searchDiagnoses } from "../lib/commonDiagnoses";

export default function DiagnosisInput({
  value,
  onChange,
  placeholder = "Escribe el diagnóstico…",
  testid = "diagnosis-input",
}) {
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(-1);
  const [matches, setMatches] = useState([]);
  const wrapRef = useRef(null);

  useEffect(() => {
    setMatches(searchDiagnoses(value));
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const select = (dx) => {
    onChange(dx.label);
    setOpen(false);
    setFocus(-1);
  };

  const onKey = (e) => {
    if (!open || matches.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocus((f) => (f + 1) % matches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocus((f) => (f - 1 + matches.length) % matches.length);
    } else if (e.key === "Enter" && focus >= 0) {
      e.preventDefault();
      select(matches[focus]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <Search
          className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-black/35"
          strokeWidth={1.75}
          weight="regular"
        />
        <input
          value={value || ""}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setFocus(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          className="w-full h-11 rounded-[14px] border border-black/[0.06] bg-white pl-10 pr-9 text-[13.5px] font-semibold text-black placeholder:text-black/35 placeholder:font-normal focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/15 transition"
          data-testid={testid}
          autoComplete="off"
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-black/[0.05] flex items-center justify-center text-black/40 transition"
            title="Limpiar"
            data-testid={`${testid}-clear`}
          >
            <X className="w-3 h-3" strokeWidth={2} weight="regular" />
          </button>
        )}
      </div>

      {open && matches.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1.5 z-20 bg-white rounded-xl border border-black/[0.08] shadow-[0_6px_24px_rgba(0,0,0,0.12)] overflow-hidden"
          data-testid={`${testid}-suggestions`}
        >
          {matches.map((dx, i) => (
            <button
              key={dx.code}
              type="button"
              onClick={() => select(dx)}
              onMouseEnter={() => setFocus(i)}
              data-testid={`${testid}-suggestion-${dx.code}`}
              className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition ${
                focus === i ? "bg-[#0071E3]/5" : "hover:bg-black/[0.03]"
              }`}
            >
              <span className="inline-flex h-6 min-w-[46px] px-1.5 items-center justify-center rounded-md bg-[#F2F3F5] text-[10.5px] font-bold text-black/70 tracking-wide flex-shrink-0">
                {dx.code}
              </span>
              <span className="text-[13px] text-black truncate">{dx.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
